import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { Button, Square, Flex, Copy } from '@grubhubprod/gh-cookbook-react';
import { noop } from 'src/utilities/noop';
import { PAGE_SIZE } from 'src/utilities/useSearchHooks';

export interface IPaginationProps {
  /**
   * Whether to disable buttons from user input
   */
  disabled?: boolean;

  /**
   *  Whether to show the "First" and "Last" page links
   */
  boundaryLinks?: boolean;

  /**
   *  Whether to show the "Next" and "Previous" page links
   */
  directionLinks?: boolean;

  /**
   *  Whether to show ellipsis symbols and first/last page numbers when maxSize > number of pages
   */
  ellipses?: boolean;

  /**
   *  Whether to rotate pages when maxSize > number of pages.
   *  Current page will be in the middle
   */
  rotate?: boolean;

  /**
   *  Number of items in collection.
   */
  collectionSize?: number;

  /**
   *  Maximum number of pages to display.
   */
  maxSize?: number;

  /**
   *  Number of items per page.
   */
  pageSize?: number;

  /**
   * An event fired when the page is changed.
   * Event's payload equals to the newly selected page.
   */
  pageChange?: (_: number) => void;

  /**
   * Pagination display size: small or large
   */
  size?: 'sm' | 'lg';

  /**
   * The page to start at.
   */
  page?: 1 | number;

  className?: string;
}

export const MAX_SIZE = 8;

/**
 * A component that will take care of visualising a pagination bar and enable / disable buttons correctly!
 */
export function Pagination({
  page = 1,
  disabled = false,
  boundaryLinks = false,
  directionLinks = true,
  ellipses = true,
  maxSize = MAX_SIZE,
  pageSize = PAGE_SIZE,
  rotate = false,
  className,
  collectionSize,
  pageChange = noop,
}: IPaginationProps): JSX.Element {
  const [selectedPage, setSelectedPage] = useState(page);
  const [pageCount, setPageCount] = useState(
    Math.ceil(collectionSize / pageSize)
  );
  const [pages, setPages] = useState<number[]>(setInitialPages(maxSize));

  useEffect(() => {
    if (page !== selectedPage) {
      setSelectedPage(page);
    }
    const totalPages = Math.ceil(collectionSize / pageSize);
    setPageCount(totalPages);
    setPages(getPages(page, totalPages, maxSize));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, collectionSize, pageSize, maxSize, selectedPage]);

  function setInitialPages(size: number): number[] {
    const pages = [];
    for (let i = 1; i <= size; i += 1) {
      pages.push(i);
    }
    return pages;
  }

  function hasPrevious() {
    return selectedPage > 1;
  }

  function hasNext() {
    return selectedPage < pageCount;
  }

  function isEllipsis(pageNumber): boolean {
    return pageNumber === -1;
  }

  /**
   * Appends ellipses and first/last page number to the displayed pages
   */
  function applyEllipses(start, end, pages, count) {
    if (ellipses) {
      if (start > 0) {
        if (start > 1) {
          pages.unshift(-1);
        }
        pages.unshift(1);
      }
      if (end < count) {
        if (end < count - 1) {
          pages.push(-1);
        }
        pages.push(count);
      }
    }
    return pages;
  }

  /**
   * Rotates page numbers based on maxSize items visible.
   * Currently selected page stays in the middle:
   *
   * Ex. for selected page = 6:
   * [5,*6*,7] for maxSize = 3
   * [4,5,*6*,7] for maxSize = 4
   */
  function applyRotation(page: number, count: number, max: number) {
    let start = 0;
    let end = count;
    const leftOffset = Math.floor(max / 2);
    const rightOffset = max % 2 === 0 ? leftOffset - 1 : leftOffset;

    if (page <= leftOffset) {
      // very beginning, no rotation -> [0..maxSize]
      end = max;
    } else if (count - page < leftOffset) {
      // very end, no rotation -> [len-maxSize..len]
      start = count - max;
    } else {
      // rotate
      start = page - leftOffset - 1;
      end = page + rightOffset;
    }

    return [start, end];
  }

  /**
   * Paginates page numbers based on maxSize items per page
   */
  function applyPagination(page: number, max: number) {
    const pag = Math.ceil(page / max) - 1;
    const start = pag * max;
    const end = start + max;

    return [start, end];
  }

  function getPages(newPage: number, count: number, max: number) {
    // fill-in model needed to render pages
    let pages = setInitialPages(count);
    // apply maxSize if necessary
    if (max > 0 && count > max) {
      let start = 0;
      let end = count;

      // either paginating or rotating page numbers
      if (rotate) {
        [start, end] = applyRotation(page, count, max);
      } else {
        [start, end] = applyPagination(page, max);
      }
      pages = pages.slice(start, end);

      // adding ellipses
      pages = applyEllipses(start, end, pages, count);
    }
    return pages;
  }

  function selectPage(pageNumber: number) {
    if (disabled) {
      return;
    }

    if (pageNumber !== selectedPage) {
      pageChange(pageNumber);
    }
  }

  function LinkItem({
    disabledWhen,
    label,
    page,
    tabindex,
    content,
  }: {
    disabledWhen: boolean;
    label: string;
    page: number;
    tabindex: number;
    content?: JSX.Element;
  }) {
    return (
      <Square
        as="li"
        border="1px solid"
        borderColor="lightLine"
        backgroundColor="untintedBackground"
        className="paginationLink"
      >
        <Button
          variant="tertiary"
          disabled={disabledWhen}
          onClick={() => selectPage(page)}
          tabIndex={tabindex}
        >
          <Square inset="x-sm">
            <span aria-label={label}>{content}</span>
          </Square>
        </Button>
      </Square>
    );
  }

  return (
    <span
      data-testid="pagination"
      role="navigation"
      className={classnames({ [className]: !!className })}
    >
      <Flex
        as="ul"
        className="paginationContainer"
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'inline-flex',
        }}
      >
        {boundaryLinks &&
          LinkItem({
            disabledWhen: !hasPrevious() || disabled,
            label: 'First',
            page: 1,
            tabindex: hasPrevious() ? null : -1,
            content: <span aria-hidden="true">&laquo;&laquo;</span>,
          })}
        {directionLinks &&
          LinkItem({
            disabledWhen: !hasPrevious() || disabled,
            label: 'Previous',
            page: selectedPage - 1,
            tabindex: hasPrevious() ? null : -1,
            content: <span aria-hidden="true">&laquo;</span>,
          })}
        {(pages || []).map((pageNumber, i) => (
          <Square
            as="li"
            className="paginationLink"
            border="1px solid"
            borderColor={
              pageNumber === selectedPage ? 'interactive' : 'lightLine'
            }
            backgroundColor={
              pageNumber === selectedPage ? 'interactive' : 'untintedBackground'
            }
            color={
              pageNumber === selectedPage
                ? 'invertedPrimaryText'
                : 'primaryText'
            }
            key={isEllipsis(pageNumber) ? `${pageNumber}${i}` : pageNumber}
          >
            {isEllipsis(pageNumber) && <span>...</span>}
            {!isEllipsis(pageNumber) && (
              <Button
                variant="tertiary"
                disabled={isEllipsis(pageNumber) || disabled}
                onClick={() => selectPage(pageNumber)}
              >
                <Square inset="x-sm">
                  <Copy
                    variant="body"
                    color={
                      pageNumber === selectedPage
                        ? 'invertedPrimaryText'
                        : 'interactive'
                    }
                  >
                    {pageNumber}
                  </Copy>
                </Square>
              </Button>
            )}
          </Square>
        ))}
        {directionLinks && (
          <LinkItem
            disabledWhen={!hasNext() || disabled}
            label="Next"
            page={selectedPage + 1}
            tabindex={hasNext() ? null : -1}
            content={<span aria-hidden="true">&raquo;</span>}
          />
        )}
        {boundaryLinks && (
          <LinkItem
            disabledWhen={!hasNext() || disabled}
            label="Last"
            page={pageCount}
            tabindex={hasNext() ? null : -1}
            content={<span aria-hidden="true">&raquo;&raquo;</span>}
          />
        )}
      </Flex>
    </span>
  );
}
